/**
 * paymentErrors.ts — safe patient-facing messages for checkout payment failures.
 *
 * Never render raw Stripe JSON, clientSecrets, stack traces, or backend internals.
 * Structured `kind` values are for tests/debugging only — not shown to patients.
 */
import { ApiError } from '../api/client';
import { MissingStripePublishableKeyError } from '../config';

export type PaymentErrorKind =
  | 'validation'
  | 'declined'
  | 'authentication'
  | 'network'
  | 'expired'
  | 'duplicate'
  | 'completed'
  | 'unavailable'
  | 'server'
  | 'unknown'
  | 'plan_changed'
  | 'safety_ack_required';

/**
 * orders-service's own 409 sentence when a `checkout/session` POST names a plan
 * the case no longer holds — another window re-planned it with Edit plan while
 * this one sat on the card step (journey UX review, 2026-09-24, O-B).
 *
 * MIRRORED VERBATIM from orders-service `CHECKOUT_PLAN_CHANGED_ELSEWHERE_MESSAGE`
 * (`src/orders/checkout-replan.ts`). It is PHI-free by construction (no order,
 * no medicine, no amount) and it already says what to do, so the portal shows
 * the server's words rather than a paraphrase — and only AFTER re-reading the
 * order, so "review your order" points at the plan orders actually holds.
 * A reword on either side is a change in BOTH repos: `paymentErrors.spec.ts`
 * pins the string.
 */
export const CHECKOUT_PLAN_CHANGED_ELSEWHERE_MESSAGE =
  'Your plan was changed in another window. Please review your order before paying.';

/**
 * orders-service's 409 when an ATTACH lost its claim because the plan moved
 * under the hold (Edit plan in another window while this one's card was being
 * set up). orders releases the hold first, then answers with this sentence
 * (`lostRoute1ClaimMessage`, the sibling of the safety-acknowledgement 409).
 * MIRRORED VERBATIM from orders-service `CHECKOUT_PLAN_CHANGED_DURING_PAYMENT_MESSAGE`
 * (`src/orders/checkout-replan.ts`); `paymentErrors.spec.ts` pins it.
 *
 * It is a stale-window refusal like the others (release validation 2026-09-25):
 * unmapped, it fell to the generic 409 and printed the card step's "not charged,
 * try again" line over the OLD plan and price, and only the NEXT press (hitting
 * the rotated SetupIntent) re-read the order. It now re-reads at once, and the
 * re-read's notice is the plan-changed one.
 */
export const CHECKOUT_PLAN_CHANGED_DURING_PAYMENT_MESSAGE =
  'The plan for this order changed while its payment was being set up. Please check your order and try again.';

/**
 * orders-service's own 409 sentence when an attach would place a month-1 hold on
 * a case that carries no safety acknowledgement (journey UX review, O-C: no hold
 * without an acknowledgement). MIRRORED VERBATIM from orders-service
 * `CHECKOUT_SAFETY_ACK_REQUIRED_MESSAGE` (`src/orders/safety-acknowledgement.ts`).
 */
export const CHECKOUT_SAFETY_ACK_REQUIRED_MESSAGE =
  'Please confirm you have read the safety information before placing your order.';

/**
 * orders-service's 400 when the bank refuses the month-1 hold at attach (journey
 * UX review, 2026-09-25). MIRRORED VERBATIM from orders-service
 * `MONTH1_HOLD_DECLINED_MESSAGE` (`src/orders/month1-hold-decline.ts`, orders
 * `fix/month1-hold-decline-is-400`), which is also the sentence its
 * `assertMonth1HoldAuthorised` sends.
 *
 * The confirmed SetupIntent never checked an amount, so the hold is the first time
 * the bank is asked, and it can say no. Nothing is held on that answer. The
 * existing 400 rule below maps it to the "declined" copy (/authorisation failed/).
 * Since 2026-09-25 `isDeclinedMonth1Hold` also reads it, BY EXACT SENTENCE, to
 * decide that the card step should set up a different card (orders
 * `retry-card`). Before orders' decline change a decline was an uncaught 500,
 * which `attachOutcomeUnknown` reads as "a hold may already be on your card".
 */
export const CHECKOUT_HOLD_DECLINED_MESSAGE = 'Card authorisation failed';

/**
 * orders-service's 409 when it will NOT set up a different card for this order
 * online (2026-09-25): no decline recorded on the case, the order is not
 * awaiting payment, or the cards-per-order cap is reached. MIRRORED VERBATIM from
 * orders-service `CHECKOUT_CARD_RETRY_UNAVAILABLE_MESSAGE`
 * (`src/orders/checkout-card-retry.ts`). It carries none of the words the generic
 * 409 rule below keys on, so on its own it would read as a "duplicate"; the card
 * step reads it through `cardRetryUnavailable` instead. A reword on either side is
 * a change in BOTH repos: `paymentErrors.spec.ts` pins the string.
 */
export const CHECKOUT_CARD_RETRY_UNAVAILABLE_MESSAGE =
  "We can't set up a different card for this order online. Please contact us and we'll help you finish your order.";

/**
 * Does a server `detail` carry this exact sentence? Case- and
 * whitespace-tolerant, never a fragment: these two sentences decide a re-read of
 * the order, so a looser match (a single word, as the `/already/` rule below
 * does) could send an unrelated refusal down that lane.
 */
function isServerSentence(detail: string | undefined, sentence: string): boolean {
  return (detail ?? '').trim().toLowerCase() === sentence.toLowerCase();
}

export type MappedPaymentError = {
  kind: PaymentErrorKind;
  message: string;
};

const MSG = {
  validation: 'Please check your card details and try again.',
  declined: 'Your card was declined. Please try a different card or contact your bank.',
  authentication:
    'Your bank needs you to confirm this payment. Please complete authentication and try again. If this keeps happening, contact support — we may need to re-authorise your card.',
  network: 'We could not reach the payment service. Check your connection and try again.',
  expired: 'This payment session has expired. Please go back and start payment again.',
  duplicate: 'This order is already being processed. Please wait a moment, then check your dashboard.',
  completed: 'This order is already complete. You can view it on your dashboard.',
  unavailable: 'Payments are temporarily unavailable. Please try again shortly.',
  server: 'Sorry — we could not complete payment. Your card has not been charged. Please try again.',
  unknown: 'Sorry — something went wrong with payment. Your card has not been charged. Please try again.',
} as const;

/** Safe patient-facing copy when payments cannot proceed (misconfigured session / missing key). */
export const PAYMENT_UNAVAILABLE_MESSAGE = MSG.unavailable;

/**
 * The card step after a DECLINED month-1 hold, once a fresh card form is up
 * (2026-09-25): the ordinary "declined" copy, which now asks for something the
 * patient can do — the form in front of them is on a new SetupIntent.
 */
export const DECLINED_TRY_ANOTHER_CARD_MESSAGE = MSG.declined;

/**
 * The card step after a DECLINED month-1 hold when orders will not set up a
 * different card online (`cardRetryUnavailable`). "Try a different card" would be
 * false here — the form cannot take one — so the patient is told the card was
 * declined and given orders' own next step.
 */
export const DECLINED_NO_DIFFERENT_CARD_MESSAGE = `Your card was declined. ${CHECKOUT_CARD_RETRY_UNAVAILABLE_MESSAGE}`;

/** Map a Stripe.js / Elements error into a safe patient message. */
export function mapStripePaymentError(error: {
  type?: string;
  code?: string;
  decline_code?: string;
  message?: string;
} | null | undefined): MappedPaymentError {
  if (!error) return { kind: 'unknown', message: MSG.unknown };

  const code = (error.code ?? '').toLowerCase();
  const decline = (error.decline_code ?? '').toLowerCase();
  const type = (error.type ?? '').toLowerCase();

  if (type === 'validation_error' || code === 'incomplete_number' || code === 'incomplete_cvc' || code === 'incomplete_expiry' || code === 'invalid_number' || code === 'invalid_expiry_month' || code === 'invalid_expiry_year' || code === 'invalid_cvc') {
    return { kind: 'validation', message: MSG.validation };
  }
  if (code === 'card_declined' || decline || code === 'expired_card' || code === 'insufficient_funds' || code === 'processing_error') {
    return { kind: 'declined', message: MSG.declined };
  }
  if (
    code === 'authentication_required' ||
    code === 'payment_intent_authentication_failure' ||
    code === 'setup_intent_authentication_failure' ||
    (type === 'invalid_request_error' && /authenticat/i.test(error.message ?? ''))
  ) {
    return { kind: 'authentication', message: MSG.authentication };
  }
  if (code === 'api_connection_error' || type === 'api_connection_error') {
    return { kind: 'network', message: MSG.network };
  }
  return { kind: 'unknown', message: MSG.unknown };
}

/**
 * STRIPE'S "THIS CARD IS ALREADY SET UP" — the SetupIntent id when a
 * `confirmSetup` refusal means exactly that, and `null` for every other error
 * (lost attach response, journey UX review 2026-09-24).
 *
 * WHEN IT HAPPENS. The patient's card was confirmed and the attach POST went to
 * orders, but the browser never heard back (a dropped connection, a timeout, a
 * 5xx). They press pay again, and Stripe refuses to confirm an intent that has
 * already succeeded: `code: 'setup_intent_unexpected_state'`, with the intent
 * itself on the error at `status: 'succeeded'`. The same answer arrives when the
 * confirm succeeded and the first attach never reached orders at all. Either way
 * the card IS set up, and the only question left is orders' — which the caller
 * answers by attaching that intent again (orders answers a retry of the SAME
 * intent idempotently: 200 'held' when it already holds the money, and it places
 * the hold when the first attach never landed).
 *
 * WHY IT MATTERS. Mapped like any other error, this refusal printed "Your card
 * has not been charged" to a patient whose card may already carry the month-1
 * hold — and invited them to try a different card.
 *
 * ALL THREE CONDITIONS, NEVER THE CODE ALONE. `setup_intent_unexpected_state`
 * also covers an intent that is NOT set up (for example `requires_payment_method`
 * after a failed confirm); attaching that one would ask orders to hold on a card
 * nobody confirmed, so it keeps its ordinary error. No id, no answer: the caller
 * must compare the id with THIS window's session before attaching anything.
 */
export function alreadySucceededSetupIntentId(
  error:
    | {
        type?: string;
        code?: string;
        message?: string;
        setup_intent?: { id?: string | null; status?: string | null } | null;
      }
    | null
    | undefined,
): string | null {
  if (!error || (error.code ?? '').toLowerCase() !== 'setup_intent_unexpected_state') return null;
  const intent = error.setup_intent;
  if (!intent || intent.status !== 'succeeded') return null;
  const id = typeof intent.id === 'string' ? intent.id.trim() : '';
  return id || null;
}

/**
 * DID THE ATTACH FAIL WITHOUT TELLING US WHETHER ORDERS HOLDS THE MONEY?
 * (lost attach response, journey UX review 2026-09-24)
 *
 * TRUE for a failure that carries no answer from orders about the order:
 *  - no HTTP response at all (fetch's `TypeError`, a dropped connection) — the
 *    request may have been handled and the answer lost on the way back;
 *  - any 5xx. orders places the hold at Stripe BEFORE it writes the ledger
 *    (attach Phase B, then Phase C), and a Phase C failure is a 500 with the hold
 *    still standing; a gateway 502/503/504 can sit on top of a request orders
 *    completed.
 *
 * FALSE for a 4xx, which is orders refusing — before any hold, or after it has
 * released one — so the existing mapped messages still apply there. That
 * includes a DECLINED hold: orders answers it 400 {@link CHECKOUT_HOLD_DECLINED_MESSAGE}
 * (the "declined" copy). ⚠️ On an orders build without that change a decline is
 * an uncaught 500 and lands HERE, telling a patient with nothing held that a hold
 * may be on their card, on every press. The portal cannot tell that 500 from a
 * Phase C one, so the fix is orders', and it must ship first.
 *
 * On TRUE the caller must not print "your card has not been charged" (nor "could
 * not reach the payment service"): see `payOutcomeUnknown` in `paymentCopy.ts`.
 */
export function attachOutcomeUnknown(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  return err.status >= 500;
}

/**
 * DID THE BANK DECLINE THE MONTH-1 HOLD? (2026-09-25) — orders' attach 400
 * {@link CHECKOUT_HOLD_DECLINED_MESSAGE}, BY EXACT SENTENCE.
 *
 * TRUE means nothing is held and orders has recorded the decline, so the card
 * step asks orders for a new card session (`retry-card`) and remounts the form on
 * it. Deliberately NOT the whole "declined" family `mapCheckoutApiError` groups
 * together: "SetupIntent is not confirmed" and "SetupIntent has no payment method"
 * are 400s about the card SETUP, not a bank's answer, and orders records no
 * decline for them — asking it to rotate would only earn its "contact us" 409.
 */
export function isDeclinedMonth1Hold(err: unknown): boolean {
  return err instanceof ApiError && err.status === 400 && isServerSentence(err.detail, CHECKOUT_HOLD_DECLINED_MESSAGE);
}

/**
 * Has the server REFUSED to set up a different card for this order online? Any 4xx
 * that is not a stale-window refusal (those re-read the order — `staleCheckoutRefusal`):
 * orders' own 409 ({@link CHECKOUT_CARD_RETRY_UNAVAILABLE_MESSAGE}), a 404 from an
 * orders build without the `retry-card` route (release skew), the guest lane's
 * 401/403 (a grant that no longer holds, or a case a claim moved onto the account),
 * a 429. The form is still on the spent intent in every one of them, so "try a
 * different card" would be false (review, 2026-09-25: it first said so on a 403).
 * Only a 5xx or no answer is FALSE: nothing was refused, and the patient's next
 * press re-attaches the declined intent, meets the same decline and asks again.
 */
export function cardRetryUnavailable(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return err.status >= 400 && err.status < 500 && staleCheckoutRefusal(err) === null;
}

/**
 * Map Orders / gateway failures after session init or payment-method attach.
 * Uses status + optional PHI-free `detail` (when parseError was enabled).
 *
 * ⚠️ An ATTACH failure goes through `attachOutcomeUnknown` first: the 5xx and
 * network messages below were written for requests that move no money.
 */
export function mapCheckoutApiError(err: unknown, fallbackMessage: string): MappedPaymentError {
  if (err instanceof MissingStripePublishableKeyError) {
    return { kind: 'unavailable', message: MSG.unavailable };
  }
  if (!(err instanceof ApiError)) {
    return { kind: 'network', message: MSG.network };
  }

  const detail = (err.detail ?? '').toLowerCase();

  if (err.status === 409) {
    // The two journey-UX refusals come FIRST and by exact sentence: each is a
    // complete, PHI-free instruction from orders, rendered as orders wrote it.
    if (isServerSentence(err.detail, CHECKOUT_PLAN_CHANGED_ELSEWHERE_MESSAGE)) {
      return { kind: 'plan_changed', message: CHECKOUT_PLAN_CHANGED_ELSEWHERE_MESSAGE };
    }
    if (isServerSentence(err.detail, CHECKOUT_PLAN_CHANGED_DURING_PAYMENT_MESSAGE)) {
      return { kind: 'plan_changed', message: CHECKOUT_PLAN_CHANGED_DURING_PAYMENT_MESSAGE };
    }
    if (isServerSentence(err.detail, CHECKOUT_SAFETY_ACK_REQUIRED_MESSAGE)) {
      return { kind: 'safety_ack_required', message: CHECKOUT_SAFETY_ACK_REQUIRED_MESSAGE };
    }
    if (/already|complete|checked out/i.test(detail)) {
      return { kind: 'completed', message: MSG.completed };
    }
    if (/mismatch|does not match|actionable|no longer payable/i.test(detail)) {
      return { kind: 'expired', message: MSG.expired };
    }
    // Generic conflict (assessment not checkout-able, etc.) — caller fallback keeps TA-06 retry copy.
    return { kind: 'duplicate', message: fallbackMessage || MSG.duplicate };
  }
  if (err.status === 404) {
    return { kind: 'expired', message: MSG.expired };
  }
  if (err.status === 400) {
    if (/authenticat/i.test(detail)) {
      // Backend hold entered requires_action without exposing a PI client_secret.
      return { kind: 'authentication', message: MSG.authentication };
    }
    if (/not confirmed|no payment method|authorisation failed|authorization failed|declined/i.test(detail)) {
      return { kind: 'declined', message: MSG.declined };
    }
    if (/expired|session|initialis/i.test(detail)) {
      return { kind: 'expired', message: MSG.expired };
    }
    return { kind: 'validation', message: MSG.validation };
  }
  if (err.status === 502 || err.status === 503) {
    return { kind: 'unavailable', message: MSG.unavailable };
  }
  if (err.status >= 500) {
    return { kind: 'server', message: fallbackMessage || MSG.server };
  }
  return { kind: 'server', message: fallbackMessage || MSG.server };
}

/**
 * A checkout refusal that means THIS WINDOW'S PICTURE OF THE ORDER IS STALE — the
 * order moved on the server while the patient sat on the card step (journey UX
 * review 2026-09-24, P-B).
 *
 * The answer to every one of them is the same, and it is not an error message
 * over the screen the patient was already looking at: CheckoutFlow re-reads
 * `checkout/resume` and renders what orders holds NOW. Rebuilding the pay step
 * from the browser's own `choice` after one of these is how a stale tab printed
 * a plan and a price orders was not going to hold, and (before orders #245) how
 * a second order and a second month-1 hold could be minted.
 *
 *  - `plan_changed`        — `checkout/session` 409: another window re-planned
 *                            the case, so this one's plan is not the case's;
 *                            or the attach 409 saying the plan changed while
 *                            its payment was being set up (hold released).
 *  - `safety_ack_required` — attach 409: the case carries no acknowledgement
 *                            (a product re-plan elsewhere cleared it).
 *  - `session_rotated`     — attach 409: the SetupIntent this window confirmed is
 *                            no longer the case's. orders rotates it on every
 *                            re-plan (O-A), so a stale window's card can never
 *                            hold the new plan's price.
 *  - `already_placed`      — `checkout/session` / attach 409 naming an order that
 *                            is already placed (orders #245) or already checked
 *                            out: the re-read lands on the account step or the
 *                            confirmation, never on a live pay control.
 *
 * Anything else is `null` and keeps its existing handling. Only 409s qualify: a
 * 404 is the assessment being gone (the plan-link lane's "link expired"), and a
 * 5xx says nothing about the order's state.
 */
export type StaleCheckoutRefusal = 'plan_changed' | 'safety_ack_required' | 'session_rotated' | 'already_placed';

export function staleCheckoutRefusal(err: unknown): StaleCheckoutRefusal | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  if (isServerSentence(err.detail, CHECKOUT_PLAN_CHANGED_ELSEWHERE_MESSAGE)) return 'plan_changed';
  // The attach's lost-claim twin: the plan moved while the card was being set
  // up, and orders released the hold before answering. Same re-read.
  if (isServerSentence(err.detail, CHECKOUT_PLAN_CHANGED_DURING_PAYMENT_MESSAGE)) return 'plan_changed';
  if (isServerSentence(err.detail, CHECKOUT_SAFETY_ACK_REQUIRED_MESSAGE)) return 'safety_ack_required';
  const detail = err.detail ?? '';
  // orders' attach refusal, verbatim: 'SetupIntent does not match this payment session'.
  if (/does not match this payment session/i.test(detail)) return 'session_rotated';
  if (/already/i.test(detail)) return 'already_placed';
  return null;
}
